import moment from 'moment-timezone';
import { useIntl } from 'react-intl';
import { IconTooltip, TooltipButton } from 'sonata-react-components';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { formatMoney } from '@/components/driveToStore/common/helpers/formatters';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

import type { CSSProperties } from 'react';
import type { MetricFilter, StoreRow } from '@/types/footfall';

import './storeHeatmap.scss';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_ABBRS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function buildDays(weekStart: string, timezone: string): Array<{ isoDate: string; key: string; label: string }> {
  return DAY_KEYS.map((key, i) => {
    const date = moment.tz(weekStart, timezone).add(i, 'days');
    return { isoDate: date.format('YYYY-MM-DD'), key, label: `${DAY_ABBRS[i]} ${date.date()}` };
  });
}

const AVERAGE_KEY = 'average';

const HEATMAP_AVG_STEPS = [
  '#E4D9FF',
  '#D4C9FF',
  '#C3A0FF',
  '#B59BFF',
  '#A385FF',
  '#9679EF',
  '#735EBB',
  '#594A90',
  '#453B6E'
];

const HEATMAP_STEPS = [
  '#F3F5FE',
  '#D3E1FB',
  '#BED5F4',
  '#8DB8EC',
  '#72A0E7',
  '#6792D2',
  '#5172A4',
  '#3E597F',
  '#3D4461'
];

function interpolateHeatmapColor(value: number, maxValue: number, palette: string[]): string {
  if (maxValue === 0) {
    return palette[0] ?? '#E4D9FF';
  }
  const intensity = Math.min(1, Math.max(0, value / maxValue));
  const index = Math.round(intensity * (palette.length - 1));
  return palette[index] ?? palette[palette.length - 1] ?? '#E4D9FF';
}

function getCellStyle(value: number | null, maxValue: number, isAverage: boolean): CSSProperties {
  if (value === null) {
    return {};
  }
  const palette = isAverage ? HEATMAP_AVG_STEPS : HEATMAP_STEPS;
  const backgroundColor = interpolateHeatmapColor(value, maxValue, palette);
  const intensity = maxValue > 0 ? value / maxValue : 0;
  const paletteIndex = Math.round(intensity * (palette.length - 1));
  const color =
    paletteIndex > Math.floor((palette.length - 1) / 2)
      ? 'var(--color-sonata-secondary-white, #FFF)'
      : 'var(--color-sonata-primary-800, #162237)';
  return { backgroundColor, color };
}

function computeMaxValue(stores: StoreRow[]): number {
  return stores.reduce((max, store) => {
    const dayMax = DAY_KEYS.reduce((m, key) => {
      const val = store.metricByDay[key];
      return val !== null && val !== undefined && val > m ? val : m;
    }, max);
    const avg = store.metricByDay[AVERAGE_KEY];
    return avg !== null && avg !== undefined && avg > dayMax ? avg : dayMax;
  }, 0);
}

type Props = {
  stores: StoreRow[];
  weekStart: string;
  metricFilter?: MetricFilter;
  rangeValue?: [number, number];
  timezone: string;
  onOpenReport?: (poiId: string) => void;
};

export default function StoreHeatmap({
  stores,
  weekStart,
  metricFilter,
  rangeValue,
  timezone,
  onOpenReport
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const days = buildDays(weekStart, timezone);
  const todayIso = moment.tz(timezone).format('YYYY-MM-DD');
  const maxValue = computeMaxValue(stores);

  const formatCellValue = (value: number): string => {
    if (metricFilter === 'cpsv') {
      return formatMoney(value, baseCurrency);
    }
    if (metricFilter === 'percentage') {
      return normalize(value, 2);
    }
    return normalize(Math.round(value), 0);
  };

  return (
    <div className="storeHeatmap">
      <div className="storeHeatmap__layout">
        <div className="storeHeatmap__storeList">
          <div className="storeHeatmap__storeListHeader" />
          {stores.map(store => (
            <div className="storeHeatmap__storeRow" key={`store-${store.rank}`}>
              <span className="storeHeatmap__rank">{store.rank}</span>
              <div className="storeHeatmap__storeBasicInfo">
                <span className="storeHeatmap__storeName" title={store.name}>
                  {store.name}
                </span>
                <span className="storeHeatmap__storeAddress" title={store.address}>
                  {store.address}
                </span>
              </div>
              <div className="storeHeatmap__storeAction">
                {store.poiId !== undefined && onOpenReport !== undefined && (
                  <TooltipButton
                    buttonConfig={{
                      buttonOnClick: () => onOpenReport(store.poiId!),
                      buttonShape: 'link',
                      buttonSize: 'small'
                    }}
                    icon="fa-duotone fa-solid fa-store"
                    iconColors={{
                      hoverIconBackgroundColor: '',
                      hoverIconColor: '',
                      iconColor: 'var(--color-sonata-interaction-500)'
                    }}
                    id={`open-report-${store.rank}`}
                    isButton
                    tooltipConfig={{ tooltipEvent: 'mouseenter', tooltipPlace: 'top' }}
                  >
                    {formatMessage({
                      defaultMessage: 'Open single report',
                      id: 'driveToStore.aggregatedReport.storeHeatmap.tooltip'
                    })}
                  </TooltipButton>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Right: independent heatmap grid ── */}
        <div className="storeHeatmap__heatmapSection">
          {/* Day headers */}
          <div className="storeHeatmap__heatmapHeader">
            {days.map(day => {
              const isOutside = stores[0]?.isOutsidePeriod?.[day.key] ?? false;
              const isNoData = !isOutside && stores.length > 0 && stores.every(s => s.isNoData?.[day.key] ?? false);
              const headerColor = isOutside
                ? 'var(--color-status-disabled-400, #E7E8EE)'
                : isNoData
                  ? 'var(--color-sonata-primary-500, #7B9FE1)'
                  : undefined;
              return (
                <div
                  className="storeHeatmap__dayHeader"
                  key={day.key}
                  style={headerColor ? { color: headerColor } : undefined}
                >
                  {day.label}
                </div>
              );
            })}
            <div className="storeHeatmap__dayHeader storeHeatmap__dayHeader--average">Avg</div>
          </div>

          {/* Heatmap rows — real data */}
          {stores.map(store => {
            const avgValue = store.metricByDay[AVERAGE_KEY] ?? null;
            const allDaysOutside = DAY_KEYS.every(key => store.isOutsidePeriod?.[key] ?? false);
            return (
              <div className="storeHeatmap__heatmapRow" key={store.rank}>
                {days.map(day => {
                  const value = store.metricByDay[day.key] ?? null;
                  const isClosed = store.isClosed?.[day.key] ?? false;
                  const isOutside = store.isOutsidePeriod?.[day.key] ?? false;
                  const isNoData = store.isNoData?.[day.key] ?? false;

                  if (isClosed) {
                    return (
                      <div className="storeHeatmap__cell storeHeatmap__cell--closed" key={day.key}>
                        <span className="storeHeatmap__cellClosed">Closed</span>
                      </div>
                    );
                  }

                  if (isOutside) {
                    return (
                      <div className="storeHeatmap__cell storeHeatmap__cell--outsidePeriod" key={day.key}>
                        <span className="storeHeatmap__cellIcon">
                          <IconTooltip
                            icon="fa-regular fa-ban"
                            iconColor="var(--color-sonata-primary-500) !important"
                            place="top"
                          >
                            {formatMessage({
                              defaultMessage: 'This date is not part of the selected campaign period',
                              id: 'driveToStore.aggregatedReport.storeHeatmap.outsidePeriod'
                            })}
                          </IconTooltip>
                        </span>
                      </div>
                    );
                  }

                  if (isNoData) {
                    const isToday = day.isoDate === todayIso;
                    return (
                      <div className="storeHeatmap__cell storeHeatmap__cell--noData" key={day.key}>
                        <span className="storeHeatmap__cellIcon">
                          <IconTooltip
                            icon="fa-regular fa-clock"
                            iconColor="var(--color-sonata-primary-500) !important"
                            place="top"
                          >
                            {isToday
                              ? formatMessage({
                                  defaultMessage: 'Calculated at end of day',
                                  id: 'driveToStore.aggregatedReport.storeHeatmap.noDataToday'
                                })
                              : formatMessage({
                                  defaultMessage: 'Not available yet',
                                  id: 'driveToStore.aggregatedReport.storeHeatmap.noData'
                                })}
                          </IconTooltip>
                        </span>
                      </div>
                    );
                  }

                  const isOutOfRange =
                    value !== null && rangeValue !== undefined && (value < rangeValue[0] || value > rangeValue[1]);

                  const cellStyle = isOutOfRange
                    ? {
                        backgroundColor: 'var(--color-sonata-secondary-300, #F3F3F3)',
                        color: 'var(--color-status-disabled-500, #9EA0B8)'
                      }
                    : getCellStyle(value, maxValue, false);

                  return (
                    <div className="storeHeatmap__cell" key={day.key} style={cellStyle}>
                      {value !== null ? formatCellValue(value) : '—'}
                    </div>
                  );
                })}

                {avgValue === null && allDaysOutside ? (
                  <div className="storeHeatmap__cell storeHeatmap__cell--average storeHeatmap__cell--outsidePeriod">
                    <span className="storeHeatmap__cellIcon">
                      <IconTooltip
                        icon="fa-regular fa-ban"
                        iconColor="var(--color-sonata-primary-500) !important"
                        place="top"
                      >
                        {formatMessage({
                          defaultMessage: 'This date is not part of the selected campaign period',
                          id: 'driveToStore.aggregatedReport.storeHeatmap.outsidePeriod'
                        })}
                      </IconTooltip>
                    </span>
                  </div>
                ) : avgValue === null ? (
                  <div className="storeHeatmap__cell storeHeatmap__cell--average storeHeatmap__cell--noData">
                    <span className="storeHeatmap__cellIcon">
                      <IconTooltip
                        icon="fa-regular fa-clock"
                        iconColor="var(--color-sonata-primary-500) !important"
                        place="top"
                      >
                        {formatMessage({
                          defaultMessage: 'No available yet',
                          id: 'driveToStore.aggregatedReport.storeHeatmap.noData'
                        })}
                      </IconTooltip>
                    </span>
                  </div>
                ) : (
                  <div
                    className="storeHeatmap__cell storeHeatmap__cell--average"
                    style={getCellStyle(avgValue, maxValue, true)}
                  >
                    {formatCellValue(avgValue)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
